/**
 * React Router v6 config — code-defined routes per PLAN-react-router-migration.md.
 *
 * Strategy: most authenticated routes render the existing `<App />` component
 * unchanged. `App.tsx` uses `useAppView()` (this PR) which reads view-state
 * from the URL when the migration flag is on, so URL becomes the source of
 * truth without rewriting App's 1,200+ call sites. Dedicated routes —
 * `/sign-in/*`, `/sign-up/*`, `/beheerconsole`, `*` (404) — have their own
 * components.
 *
 * Lazy-loading: SignIn/SignUp/Beheerconsole/NotFound are split via React.lazy
 * to shave initial bundle. The main `<App />` shell stays in the entry chunk
 * because it owns the authed layout and is hit on every authed pageview.
 */

import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import App from '../../App';
import { trackEvent } from '../../services/analytics';

const SignInRoute = lazy(() => import('./pages/SignIn'));
const SignUpRoute = lazy(() => import('./pages/SignUp'));
const BeheerconsoleRoute = lazy(() => import('./pages/Beheerconsole'));
const NotFoundRoute = lazy(() => import('./pages/NotFound'));

// D-08: /account/* routes — embedded Clerk drop-ins, routing="path", Account Portal OFF
const AccountLayout = lazy(() => import('./pages/account/AccountLayout'));
const AccountProfile = lazy(() => import('./pages/account/Profile'));
const AccountSecurity = lazy(() => import('./pages/account/Security'));
const AccountSessions = lazy(() => import('./pages/account/Sessions'));
const AccountOrganizations = lazy(() => import('./pages/account/Organizations'));
const AccountCreateOrganization = lazy(() => import('./pages/account/CreateOrganization'));

function RouteLoader() {
  return (
    <div
      className="min-h-screen bg-bg flex items-center justify-center"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Loader2 size={24} className="animate-spin text-ink/40" aria-hidden="true" />
      <span className="sr-only">Laden…</span>
    </div>
  );
}

/** Fires a pageview event on every route change. */
function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => {
    try {
      trackEvent('pageview', { path: location.pathname });
    } catch {
      // analytics is best-effort
    }
  }, [location.pathname]);
  return null;
}

function RootLayout() {
  return (
    <>
      <RouteAnalytics />
      <Outlet />
    </>
  );
}

function AuthAliasRedirect({ to }: { to: '/sign-in' | '/sign-up' }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search, hash: location.hash }} replace />;
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Legacy marketing aliases — keep direct UAT links off the 404 page.
      { path: '/login/*', element: <AuthAliasRedirect to="/sign-in" /> },
      { path: '/signup/*', element: <AuthAliasRedirect to="/sign-up" /> },
      // Legacy /dashboard route — redirect to /eigendommen (CAR-62)
      { path: '/dashboard', element: <Navigate to="/eigendommen" replace /> },
      { path: '/dashboard/*', element: <Navigate to="/eigendommen" replace /> },

      // Auth — Clerk drop-in
      {
        path: '/sign-in/*',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <SignInRoute />
          </Suspense>
        ),
      },
      {
        path: '/sign-up/*',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <SignUpRoute />
          </Suspense>
        ),
      },

      // Admin — `/admin` legacy redirected to `/beheerconsole` via vercel.json
      {
        path: '/beheerconsole',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <BeheerconsoleRoute />
          </Suspense>
        ),
      },

      // Authed views — all delegate to <App /> which routes via useAppView().
      // Wizard sub-paths require :propertyId; bare /eigendommen is the manager.
      { path: '/', element: <App /> },
      { path: '/eigendommen', element: <App /> },
      { path: '/eigendommen/:propertyId/upload', element: <App /> },
      { path: '/eigendommen/:propertyId/single', element: <App /> },
      { path: '/eigendommen/:propertyId/varianten', element: <App /> },
      { path: '/eigendommen/:propertyId/studio', element: <App /> },
      { path: '/eigendommen/:propertyId/batch', element: <App /> },
      { path: '/team', element: <App /> },
      { path: '/profiel', element: <App /> },
      { path: '/abonnement', element: <App /> },
      { path: '/referrals', element: <App /> },
      { path: '/feedback', element: <App /> },

      // D-08: Account portal — embedded Clerk drop-ins (Account Portal mode OFF)
      {
        path: '/account',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <AccountLayout />
          </Suspense>
        ),
        children: [
          { index: true, element: <Navigate to="/account/profile" replace /> },
          {
            path: 'profile/*',
            element: (
              <Suspense fallback={<RouteLoader />}>
                <AccountProfile />
              </Suspense>
            ),
          },
          {
            path: 'security/*',
            element: (
              <Suspense fallback={<RouteLoader />}>
                <AccountSecurity />
              </Suspense>
            ),
          },
          {
            path: 'sessions/*',
            element: (
              <Suspense fallback={<RouteLoader />}>
                <AccountSessions />
              </Suspense>
            ),
          },
          {
            path: 'organizations/create/*',
            element: (
              <Suspense fallback={<RouteLoader />}>
                <AccountCreateOrganization />
              </Suspense>
            ),
          },
          {
            path: 'organizations/*',
            element: (
              <Suspense fallback={<RouteLoader />}>
                <AccountOrganizations />
              </Suspense>
            ),
          },
        ],
      },

      // 404 — stays last
      {
        path: '*',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <NotFoundRoute />
          </Suspense>
        ),
      },
    ],
  },
]);

export function StageryRouter() {
  return <RouterProvider router={router} />;
}
